import { logger, loggerEnabled } from '../../../../utils/common/logger'
import { DOMEvents } from '../../../../utils/dom'
import { DOMUtils } from '../../../../utils/dom'
import { Store } from '../../../../utils/storage/keys'
import { AnalyticsService } from '../../../analytics'
import { IIdentityLogoConfig, ISubscription, SSOScreen } from '../adapter'

export interface IPianoApiResponse {
    code: number
    count?: number
    data?: object[]
    limit: number
    offset: number
    total: number
    conversions?: IPianoConversion[]
}

export interface IPianoApiAccessItem {
    // `expire_date` is not always available in the `/access/list` response.
    // It's only available in `conversion/list` response
    // All already expired supscription terms are excluded from `/access/list` response
    expire_date?: number
    resource: {
        aid: string
        rid: string
    },
    term: IPianoTerm
    start_date: number
}

export interface IPianoResource {
    rid: string
    aid: string
    name: string
    description: string
    image_url?: string
}

export interface IPianoTerm {
    aid: string
    term_id: string
    resource?: IPianoResource
    name?: string
    description?: string
    type?: string
}

export interface IPianoUserAccess {
    granted: boolean
    start_date: number
    expire_date?: number
}

export interface IPianoConversion {
    aid: string
    term?: IPianoTerm
    term_conversion_id?: string
    type?: string
    user_access: IPianoUserAccess
}

export interface IWindow extends Window {
    tp: any
}

declare let window: IWindow

enum PianoCheckoutState {
    ALREADY_HAS_ACCESS = 'alreadyHasAccess',
    CHECKOUT_COMPLETED = 'checkoutCompleted',
    VOUCHER_REDEMPTION_COMPLETED = 'voucherRedemptionCompleted',
    CLOSE = 'close'

}

enum PianoModuleName {
    PREMIUM_SHOW_OFFER = 'Premium Show Offer'
}

enum FocusableElements {
    MAINSITE = 'a, main, input, select, textarea, details, audio, video, [tabindex="0"], button:not(.tp-close)',
    MODAL = '.tp-iframe-wrapper.tp-active',
    MAIN = 'main'
}

export abstract class BasePianoSubscriberService {
    protected openCheckoutAfterLogin = false
    protected pianoEnabled = true
    protected pianoPreconfigured: boolean
    protected subscribeButton: HTMLButtonElement

    protected pianoEndpoints = {
        access: '/access/list',
        conversions: '/conversion/list',
        externalCreate:'/conversion/external/create',
        register: '/conversion/registration/create'
    }
    protected pianoExpUrl = '//experience.tinypass.com/xbuilder/experience/load?aid='
    protected pianoSandboxUrl = '//sandbox.tinypass.com/xbuilder/experience/load?aid='
    protected pianoReady: Promise<boolean>
    protected registrantTermId: string
    protected userToken: string
    protected pianoUser: {
        email?: string
    }
    protected meterData: {
        expired?: boolean,
        maxViews?: number,
        views?: number
    }

    public abstract getAccess(): Promise<ISubscription[]>

    public abstract getConversions(): any

    /**
     * Close Piano modal
     */
    public closeCheckoutModal() {
        window.tp.offer.close()
        this.setFocusOnMainTabs()
    }

    /**
     * Shows Piano paywall
     * @param offerId
     * @param templateId
     */
    public showPaywall(offerId: string, templateId: string) {
        window.tp.push(['init', () => {
            window.tp.offer.show({
                displayMode: 'modal',
                offerId,
                showCloseButton: false,
                templateId
            })
        }])
    }

    /**
     * Open the Piano checkout modal
     */
    public showCheckoutModal(offerId: string, termId: string, button: HTMLButtonElement) {
        this.subscribeButton = button
        if (offerId && termId) {
            window.tp.push(['init', () => {
                window.tp.offer.startCheckout({
                    offerId,
                    termId
                })
                this.setFocusOnModalTabs()
            }])
        }
    }

    /**
     * Focus to Piano checkout modal
     */
    public setFocusOnModalTabs() {
        const el = document.querySelectorAll<HTMLElement>(FocusableElements.MODAL)
        if (el) {
            try {
                DOMUtils.disableTabs(FocusableElements.MAINSITE)
                DOMUtils.updateTabs(el, 0)
                el[0].focus()
            } catch (e) {
                logger('error', ['cannot focus to modal', e])
            }
        }
    }

    public setFocusOnMainTabs() {
        DOMUtils.enableTabs(FocusableElements.MAINSITE)
        try {
            if (this.subscribeButton) {
                this.subscribeButton.focus()
            } else {
                const main = document.querySelector<HTMLButtonElement>(FocusableElements.MAIN)
                main.tabIndex = 0
                main.focus()
            }
        } catch (e) {
            logger('error', ['cannot focus to modal', e])
        }
    }

    /**
     * Register a user for one-digital registrant access, if they don't have it yet.
     * @param aid: application aid required for Piano API call
     * @param isLoginRadius - true if the user is logged in via LoginRadius
     * https://docs.piano.io/api?endpoint=get~2F~2Fconversion~2Fregistration~2Fcreate
     */
    public async registerForOneDigital(aid: string) {
        const query = {
            aid,
            term_id: this.registrantTermId,
            user_token: this.userToken
        }
        await this.pianoCallApi(this.pianoEndpoints.register, query)
        window.tp.user.refreshAccessToken(true)
    }

    /**
     * Calls Piano API via global object.
     */
    public async pianoCallApi(endpoint: string, data?: any): Promise<IPianoApiResponse> {
        window.tp = window.tp || []
        logger('debug', 'PianoCallApi: LoginRadius user detected')
        await new Promise<void>((resolve) => {
            window.tp.push(['init', () => {
                logger('debug', 'PianoCallApi: Piano ready')
                resolve()
            }])
        })

        return new Promise((resolve, reject) => {
            try {
                window.tp.api.callApi(endpoint, data, (res: IPianoApiResponse) => {
                    // tslint:disable-next-line:no-magic-numbers
                    if (res.code !== 200 && res.code !== 0) {
                        reject(new Error(`Piano API failure: ${JSON.stringify(res)}`))
                    } else {
                        resolve(res)
                    }
                })
            } catch (e) {
                reject(e)
            }
        })
    }

    /**
     * Adds piano callback handlers using the window.tp object
     * @param vendor - optional, used to specify that the vendor is loginradius to not add a loginRequired handler
     */
    public addPianoHandlers(vendor?: string, abTestEnabled = false) {
        // call the confirm_referral endpoint and send an analytic event for each new subscription
        // https://docs.piano.io/callbacks/#completeevent
        window.tp.push(['addHandler','showTemplate', () => {
            // Your code after template has been shown
            // set focus to modals
            this.setFocusOnModalTabs()
        }])
        window.tp.push(['addHandler', 'checkoutComplete', () => {
            document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                detail: {
                    control_fields: {
                        mparticle: {
                            mp_event_type: 'UserContent'
                        },
                        permutive: {}
                    },
                    event_name: AnalyticsService.customEventNames.subscription,
                }
            }))
            // remove profile to force update
            localStorage.removeItem(Store.ID)
        }])

        // send an analytic event for checkout modal
        // https://docs.piano.io/callbacks/#startevent
        window.tp.push(['addHandler', 'startCheckout', () => {
            this.setFocusOnModalTabs()
        }])
        // send analytic event for checkout state change
        // https://docs.piano.io/callbacks/#statechangeevent
        window.tp.push(['addHandler', 'checkoutStateChange', (event: any) => {
            document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                detail: {
                    control_fields: {
                        mparticle: {
                            keys: {
                                modal_name: 'modal_name'
                            },
                            mp_event_type: 'UserPreference'
                        },
                        permutive: {
                            keys: {
                                modal_name: 'modal_name'
                            }
                        }
                    },
                    event_name: AnalyticsService.customEventNames.modalView,
                    modal_name: event.stateName === 'state2' ? 'start_checkout' : event.stateName
                }
            }))
        }])

        // reload the page after completing checkout and closing the modal
        // https://docs.piano.io/callbacks/#closeevent
        window.tp.push(['addHandler', 'checkoutClose', (checkoutCloseData: any) => {
            if (checkoutCloseData.state !== PianoCheckoutState.CHECKOUT_COMPLETED) {
                document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                        detail: {
                            checkout_state: checkoutCloseData.state,
                            control_fields: {
                                mparticle: {
                                    keys: {
                                        checkout_state: 'checkout_state'
                                    },
                                    mp_event_type: 'UserContent'
                                }
                            },
                            event_name: AnalyticsService.customEventNames.checkoutClosed,
                        }
                    }
                ))
            }
            switch (checkoutCloseData.state) {
                case PianoCheckoutState.CHECKOUT_COMPLETED:
                case PianoCheckoutState.ALREADY_HAS_ACCESS:
                case PianoCheckoutState.VOUCHER_REDEMPTION_COMPLETED:
                    window.location.reload()
                    break
                case PianoCheckoutState.CLOSE:
                    // User did not complete the purchase and simply closed the modal
                    // TODO: show more attractive offer
                    break
                }
            // if site behind disabled, enable
            this.closeCheckoutModal()
            // if a button was stored, return focus or focus main
        }])

        // send an analytics event when encountering a checkout error
        window.tp.push(['addHandler', 'checkoutPaymentError', (errorData: any) => {
            document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                detail: {
                    control_fields: {
                        mparticle: {
                            keys: {
                                error_message: 'error_message',
                                form_name: 'form_name',
                            },
                            mp_event_type: 'UserContent'
                        }
                    },
                    error_message: `${errorData.message}`,
                    event_name: AnalyticsService.customEventNames.submissionError,
                    form_name: 'piano-checkout'
                }
            }))
        }])

        // https://docs.piano.io/callbacks/#experienceexecute
        window.tp.push(['addHandler', 'experienceExecute', (experience: any) => {
            let variantName
            const experienceMeterExpiredObject = experience.result.events.filter((event: { eventType: string }) =>
                event.eventType === 'meterExpired')
            const meterExpired = experienceMeterExpiredObject.length > 0
            // taking care of piano AB testing
            if (abTestEnabled && experience.result.experiences.length > 0 && !meterExpired) {
                const experienceAB = experience.result.events.filter((event: { eventType: string }) =>
                    event.eventType === 'experienceExecute')
                if (experienceAB && experienceAB.length > 0) {
                    const currentMeterName = experienceAB[0].eventExecutionContext.currentMeterName || undefined
                    if (experienceAB[0].eventExecutionContext.splitTests.length > 0) {
                        variantName = experienceAB[0].eventExecutionContext.splitTests[0].variantName
                        document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                            detail: {
                                control_fields: {
                                    mparticle: {
                                        keys: {
                                            meter_name: 'meter_name',
                                            variant_name: 'variant_name'
                                        },
                                        mp_custom_flags: ['Google.NonInteraction', 'Google.Page'],
                                        mp_event_type: 'UserPreference'
                                    }
                                },
                                event_name: AnalyticsService.customEventNames.meterExecute,
                                meter_name: `${currentMeterName}`,
                                variant_name: variantName
                            }
                        }))
                    }
                }
            }

            if (meterExpired) {
                const meterData = experienceMeterExpiredObject[0].eventParams
                document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                    detail: {
                        control_fields: {
                            mparticle: {
                                keys: {
                                    meter_name: 'meter_name',
                                    modal_name: 'modal_name',
                                    variant_name: 'variant_name'
                                },
                                mp_custom_flags: ['Google.NonInteraction', 'Google.Page'],
                                mp_event_type: 'UserPreference'
                            },
                            permutive: {
                                keys: {
                                    modal_name: 'modal_name'
                                }
                            }
                        },
                        event_name: AnalyticsService.customEventNames.modalView,
                        meter_name: `${meterData.meterName}`,
                        modal_name: 'paywall_limit_reached',
                        variant_name: variantName
                    }
                }))
                this.handleMeterExpired(meterData)
            }
            // send an analytic event for premium content
            const premium = experience.result.events.filter( (event: { eventModuleParams: { moduleName: string } }) =>
                event.eventModuleParams.moduleName === PianoModuleName.PREMIUM_SHOW_OFFER )
            if (premium.length > 0) {
                document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                    detail: {
                        control_fields: {
                            mparticle: {
                                keys: {
                                    modal_name: 'modal_name',
                                },
                                mp_custom_flags: ['Google.NonInteraction', 'Google.Page'],
                                mp_event_type: 'UserPreference'
                            }
                        },
                        event_name: AnalyticsService.customEventNames.modalView,
                        modal_name: 'paywall_premium_content'
                    }
                }))
            }
            this.handleExperienceExecute(experience)
        }])

        // https://docs.piano.io/callbacks/#activeevent
        window.tp.push(['addHandler', 'meterActive', (meterData: any) => {
            this.handleMeterActive(meterData)
        }])

        // send an analytic event for paywall limit modal
        // https://docs.piano.io/callbacks/#expiredevent
        // moved to experience execute

        if (vendor === 'loginradius') {
            window.tp.push(['addHandler', 'loginRequired', (params: any) => {
                // Redirect to login page
                const redirectUrl = location.origin
                const screen = (params && params.startScreen) ? params.startScreen : ''
                if (sessionStorage) {
                    sessionStorage.setItem(Store.REDIRECT_URL, location.href)
                    // store params of offer in storage before redirect
                    if (params && params.offerId && params.termId) {
                        sessionStorage.setItem(Store.PIANO_PARAMS, JSON.stringify(params))
                        sessionStorage.setItem(Store.USER_WANTS, 'subscribe')
                    }
                }
                if (screen === SSOScreen.LOG_IN) {
                    location.assign(`${redirectUrl}/sign-in`)
                } else {
                    location.assign(`${redirectUrl}/register`)
                }
            }])
        }
    }

    /**
     * Push Piano tags
     */
    public async pianoPushTags(isPremium: boolean, isPaywallWhitelisted: boolean) {
        const tags: string[] = []
        if (isPremium) {
            tags.push('premium_locked')
        } else if (isPaywallWhitelisted) {
            tags.push('not_metered')
        } else {
            tags.push('metered')
        }

        await this.pianoReady

        return new Promise<void>((resolve, reject) => {
            try {
                window.tp.push(['setTags', tags])
                resolve()
            } catch (e) {
                reject(e)
            }
        })
    }

    /**
     * Push product logo url for Piano templates
     */
    public pianoPushLogoUrl(logoUrl: IIdentityLogoConfig) {
        window.tp.push(['setCustomVariable', 'logoUrl', logoUrl.default])
        logger('debug', 'logoURL: ' + logoUrl)
    }

    public handleMeterActive(meterData: any) {
        this.meterData = {
            expired: false,
            maxViews: meterData.maxViews,
            views: meterData.views
        }
        this.logMeterData()
        document.dispatchEvent(new CustomEvent(DOMEvents.PIANO_METER_EVENT,
            { detail: { meterData: this.meterData } }))
    }

    public handleMeterExpired(meterData: any) {
        this.meterData = {
            expired: true,
            maxViews: meterData.maxViews,
            views: meterData.views
        }
        this.logMeterData()
        document.dispatchEvent(new CustomEvent(DOMEvents.PIANO_METER_EVENT,
            { detail: { meterData: this.meterData } }))
    }
    protected handleExperienceExecute(experience: any) {
        this.pianoUser = {
            email: experience.user.email
        }
        this.logMeterData()
    }

    protected logMeterData() {
        // log meter data in console, when all data is available
        // log only on stage/dev or if debug=logging param is in URL for prod
        if (this.pianoUser && this.meterData && (__NON_PROD || loggerEnabled())) {
            const isUserLoggedIn = this.pianoUser.email.toLowerCase() !== 'n/a'
            const logMessagePrefix = isUserLoggedIn ? 'Paywall' : 'Registration'
            const logMessageSuffix = this.meterData.expired ? 'hard gate' : 'article counter'
            const logUserEmail = isUserLoggedIn ? this.pianoUser.email : 'not logged in'
            const logMeterData = `${this.meterData.views} of ${this.meterData.maxViews} free metered articles read`
            const css = isUserLoggedIn ?
                'padding: 3px 10px 3px 10px; font-size:15px; background-color:green; color:white;' :
                'padding: 3px 10px 3px 10px; font-size:15px; background-color:red; color:black;'
            const message = `${logMessagePrefix} ${logMessageSuffix} - ${logUserEmail} - ${logMeterData}`
            console.log('%c%s', css, message)
        }
    }

}
